import React, { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { TourCardFragment } from "@graphql/types";
import { getDuration } from "@helpers/getDuration";
import { getCurrencyConversion } from "@helpers/getCurrencyConversion";
import { useRouter } from "next/router";
import Link from "next/link";
import { Card } from "@components/ui/Card";
import { Avatar } from "@components/ui/Avatar";
import { Rating } from "@components/ui/Rating";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { twMerge } from "tailwind-merge";
import { Image } from "@components/ui/Image";
import { Divider } from "@components/ui/Divider";

type IProps = {
  showUser?: boolean;
  onRatingClicked?: () => void;
  ratingVariant?: "default" | "stars";
  tour: TourCardFragment;
  variant: "horizontal" | "vertical" | "old_vertical";
  className?: HTMLAttributes<HTMLDivElement>["className"];
};

const TourCard: FC<IProps> = ({
  showUser,
  onRatingClicked,
  ratingVariant = "default",
  tour,
  variant,
  className,
}) => {
  const variantValue = variant || "vertical";
  const router = useRouter();
  const { query } = router;

  let tourUrl = tour.url;

  // concat filters value to tour url
  if (
    (query.t === undefined ||
      query.t === "tours" ||
      query.t === "transportation" ||
      query.t === "online-tours") &&
    (query.date !== "" || query.people !== "")
  ) {
    const queryParams = {
      ...(query.date !== undefined && { date: query.date }),
      ...(query.adults !== undefined && { adults: query.adults }),
      ...(query.children !== undefined && { children: query.children }),
      ...(query.infants !== undefined && { infants: query.infants }),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryString = new URLSearchParams(queryParams as any).toString();
    tourUrl = queryString ? `${tourUrl}?${queryString}` : tourUrl;
  }

  const tourCoverImage =
    tour.picture?.url || tour.gallery?.find((_, i) => i === 0)?.src;

  if (variantValue === "old_vertical") {
    return (
      <Link
        href={getAbsoluteUrl(tourUrl)}
        target="_blank"
        className={twMerge("flex flex-1", className)}
      >
        <Card className="flex flex-col group mb-0 max-sm:mb-4">
          <div className="relative w-full bg-gray-200">
            <div
              className="relative w-full"
              style={{ paddingBottom: "75%" /* 4:3 aspect */ }}
            >
              <Image
                src={tourCoverImage}
                alt={tour.title}
                className="w-full object-cover h-48"
                fill={true}
                activeCache={false}
              />
            </div>
          </div>
          <Card.Container className="flex flex-1 flex-col">
            <Card.Body className="flex flex-col h-60">
              <Card.Title className="h-14 truncate-2 mb-3">
                {tour.title}
              </Card.Title>
              <Card.Tags>
                {tour.city && <Card.Tag>{tour.city.name}</Card.Tag>}
                {tour.duration && (
                  <Card.Tag>{getDuration(tour.duration, "short")}</Card.Tag>
                )}
              </Card.Tags>
              <Card.Description className="truncate-3 h-16">
                {tour.overview || tour.content}
              </Card.Description>
              <Card.Price className="mb-4">
                <span className="text-gray-500">FROM</span>
                <b
                  className="text-gray-800 text-lg mx-2"
                  suppressHydrationWarning
                >
                  {getCurrencyConversion(
                    tour.price.amount,
                    tour.price.currency
                  )}
                </b>
                <span className="text-gray-500">
                  {tour.price.model === "PERSON" && "/ per person"}
                  {tour.price.model === "GROUP" && "/ per group"}
                </span>
              </Card.Price>
            </Card.Body>
            {showUser && (
              <Card.Footer>
                <Avatar
                  src={tour.supplier.picture && tour.supplier.picture.url}
                  size="sm"
                  imgAlt={`${tour.country?.name} Tour Guide - ${tour.supplier.displayName}`}
                />
                <div className="ml-2">
                  <div className="text-blue-900 text-lg font-medium truncate w-44">
                    {tour.supplier.displayName}
                  </div>
                  {tour.supplier.stats.rating > 0 &&
                    tour.supplier.stats.reviewsCount > 0 && (
                      <Rating
                        rate={tour.supplier.stats.rating}
                        reviews={tour.supplier.stats.reviewsCount}
                        alwaysShowDecimals={true}
                        onClick={onRatingClicked}
                        size="sm"
                        variant={ratingVariant}
                      />
                    )}
                </div>
              </Card.Footer>
            )}
          </Card.Container>
        </Card>
      </Link>
    );
  }

  if (variantValue === "vertical") {
    const showRating =
      tour.supplier.stats.rating > 0 && tour.supplier.stats.reviewsCount > 0;

    const [titleLines, setTitleLines] = useState(0);
    const titleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (titleRef.current) {
        const lineHeight = parseFloat(
          getComputedStyle(titleRef.current).lineHeight
        );
        const height = titleRef.current.offsetHeight;
        const lines = Math.round(height / lineHeight);
        setTitleLines(lines);
      }
    }, []);

    const getPrice = (isMobile?: boolean) => {
      if (isMobile) {
        return (
          <div className={`text-xs text-gray-600 flex items-center mt-2`}>
            <span className="text-gray-500">FROM</span>
            <b className="text-gray-800 text-lg mx-2" suppressHydrationWarning>
              {getCurrencyConversion(tour.price.amount, tour.price.currency)}
            </b>
            <span className="text-gray-500">
              {tour.price.model === "PERSON" && "/ per person"}
              {tour.price.model === "GROUP" && "/ per group"}
            </span>
          </div>
        );
      }

      return (
        <div
          className={`text-xs text-gray-600 items-center hidden sm:block mt-2`}
        >
          <span className="text-gray-500">FROM</span>
          <b className="text-gray-800 text-lg mx-2" suppressHydrationWarning>
            {getCurrencyConversion(tour.price.amount, tour.price.currency)}
          </b>
          <span className="text-gray-500">
            {tour.price.model === "PERSON" && "/ per person"}
            {tour.price.model === "GROUP" && "/ per group"}
          </span>
        </div>
      );
    };

    return (
      <Link
        href={getAbsoluteUrl(tourUrl)}
        target="_blank"
        className={className}
      >
        <div className="flex flex-col bg-white border border-gray-100 rounded-md shadow-lg w-full h-full sm:mb-0">
          <div
            className="relative w-full"
            style={{ paddingBottom: "75%" /* 4:3 aspect */ }}
          >
            <Image
              key={tour.id}
              src={tourCoverImage}
              alt={tour.title}
              className="w-full object-cover h-48 rounded-t-md"
              activeCache={false}
              fill={true}
            />
            {/* City  */}
            <div className="absolute top-2 left-2 w-full pr-2 grid-rows-2">
              <div
                className="inline-block bg-gray-200 text-gray-600 rounded px-3 py-1 text-xs truncate"
                style={{ maxWidth: "40%" }}
              >
                {tour.city?.name}
              </div>
            </div>

            {/*  Duration */}
            <div className="absolute top-10 left-2 w-full pr-2 grid-rows-2">
              <div className="inline-block bg-gray-200 text-gray-600 rounded px-3 py-1 text-xs truncate">
                {getDuration(tour.duration, "short")}
              </div>
            </div>
          </div>
          <div className="flex flex-col w-full h-full p-3">
            {/* Title: In PC, if title is 1 line,overview will be 3 lines and if title is 2 lines overview will be 2 lines  */}
            <div
              className="hidden sm:block overflow-hidden"
              style={{
                maxHeight: "7rem",
              }}
            >
              <h2
                className={`text-xl font-medium truncate-2 mb-4`}
                ref={titleRef}
              >
                {tour.title}
              </h2>

              {/* Description */}
              <h3
                className={`text-sm text-gray-500 overflow-hidden ${titleLines === 2 ? "truncate-2" : "truncate-3"}`}
                style={{ maxHeight: "3.75rem" }}
              >
                {tour.overview || tour.content}
              </h3>
            </div>

            {/* Title: In mobile, title should be fixed height of 2 lines to avoid card heigh mismatch. AS there no overview we don't condition check   */}
            <div className={`flex flex-col w-full sm:hidden`}>
              <h2 className="text-xl font-medium leading-snug truncate-2">
                {tour.title}
              </h2>
              {getPrice(true)}
            </div>

            {/* Tour price */}
            <div className="flex mt-auto">{getPrice()}</div>
          </div>
          {/* FOOTER */}
          {showUser && (
            <div className="flex flex-col w-full h-20 mt-auto">
              {/* DIVIDER */}
              <div className="mx-3">
                {showUser && <Divider className="my-0" />}
              </div>
              {/*  Supplier details */}
              <div
                className={`flex items-center p-3 h-20 ${showRating ? "pt-2" : "pt-3"}`}
              >
                <Avatar
                  src={tour.supplier.picture && tour.supplier.picture.url}
                  size="sm"
                  imgAlt={`${tour.country?.name} Tour Guide - ${tour.supplier.displayName}`}
                />
                <div className="ml-2">
                  <div
                    className="text-blue-900 text-lg font-medium truncate-wrap"
                    style={{ maxWidth: "11rem" }}
                  >
                    {tour.supplier.displayName}
                  </div>

                  {showRating && (
                    <Rating
                      rate={tour.supplier.stats.rating}
                      reviews={tour.supplier.stats.reviewsCount}
                      alwaysShowDecimals={true}
                      onClick={onRatingClicked}
                      size="sm"
                      variant={ratingVariant}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={getAbsoluteUrl(tourUrl)}
      target="_blank"
      className={twMerge("flex flex-1 w-full", className)}
    >
      <Card className="flex flex-row mb-0 w-full">
        <div className="relative w-64 h-full bg-gray-200">
          {tourCoverImage && (
            <Image
              key={tour.id}
              src={tourCoverImage}
              alt={tour.title}
              className="w-full h-full object-cover"
              fill={true}
              activeCache={false}
            />
          )}
        </div>
        <Card.Container className="flex flex-col px-4 py-2 w-3/5">
          <Card.Body className="flex flex-col">
            <Card.Title className="truncate mb-3 w-72">{tour.title}</Card.Title>
            <Card.Tags>
              {tour.city && <Card.Tag>{tour.city.name}</Card.Tag>}
              {tour.duration && (
                <Card.Tag>{getDuration(tour.duration, "short")}</Card.Tag>
              )}
            </Card.Tags>
            <Card.Description className="truncate-3 h-16">
              {tour.overview || tour.content}
            </Card.Description>
            <Card.Price>
              <span className="text-gray-500">FROM</span>
              <b
                className="text-gray-800 text-lg mx-2"
                suppressHydrationWarning
              >
                {getCurrencyConversion(tour.price.amount, tour.price.currency)}
              </b>
              <span className="text-gray-500">
                {tour.price.model === "PERSON" && "/ per person"}
                {tour.price.model === "GROUP" && "/ per group"}
              </span>
            </Card.Price>
          </Card.Body>
          {showUser && (
            <Card.Footer>
              <Avatar
                src={tour.supplier.picture && tour.supplier.picture.url}
                size="sm"
                imgAlt={`${tour.country?.name} Tour Guide - ${tour.supplier.displayName}`}
              />
              <div className="ml-2">
                <div className="text-blue-900 text-lg font-medium truncate w-44">
                  {tour.supplier.displayName}
                </div>
                {tour.supplier.stats.rating > 0 &&
                  tour.supplier.stats.reviewsCount > 0 && (
                    <Rating
                      rate={tour.supplier.stats.rating}
                      reviews={tour.supplier.stats.reviewsCount}
                      alwaysShowDecimals={true}
                      onClick={onRatingClicked}
                      size="sm"
                      variant={ratingVariant}
                    />
                  )}
              </div>
            </Card.Footer>
          )}
        </Card.Container>
      </Card>
    </Link>
  );
};

export default TourCard;
